import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ArrowBack, Search as SearchIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import vehiculoService, {
  CreateVehiculoDto,
  CatalogoItemDto,
  MAX_UNIDAD_CODIGO,
} from '../../services/vehiculoService';
import tecnicoService from '../../services/tecnicoService';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import CatalogoSearchDialog from '../../components/servicio/CatalogoSearchDialog';
import CatalogoCrudDialog from '../../components/servicio/CatalogoCrudDialog';

interface VehiculoFormState extends Omit<CreateVehiculoDto, 'descripcion'> {
  descripcion: string;
  nombreCliente: string;
  descripcionTecnico: string;
  descripcionMarca: string;
  descripcionLinea: string;
  descripcionModelo: string;
  descripcionCombustible: string;
}

const defaultForm: VehiculoFormState = {
  patente: '',
  nroCliente: 0,
  nombreCliente: '',
  descripcion: '',
  kilometrajeUltimoService: null,
  fechaUltimoService: '',
  fechaProximoService: '',
  anio: null,
  nroSerieMotor: '',
  nroSerieChasis: '',
  codigoTecnico: '',
  descripcionTecnico: '',
  codigoMarca: '',
  descripcionMarca: '',
  codigoLinea: '',
  descripcionLinea: '',
  codigoModelo: '',
  descripcionModelo: '',
  codigoCombustible: '',
  descripcionCombustible: '',
};

const toDateInput = (value?: string | null): string => {
  if (!value) return '';
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

type CatalogoKind = 'tecnico' | 'marca' | 'linea' | 'modelo' | 'combustible' | null;
type CatalogoCrudKind = 'marca' | 'linea' | 'modelo' | 'combustible';

const VehiculoForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { patente: patenteParam } = useParams();
  const patenteDecoded = patenteParam ? decodeURIComponent(patenteParam) : '';
  const isEdit = !!(patenteParam && patenteParam !== 'nuevo');
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<VehiculoFormState>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openClienteSearch, setOpenClienteSearch] = useState(false);
  const [catalogoOpen, setCatalogoOpen] = useState<CatalogoKind>(null);

  const dialogsOpen = openClienteSearch || !!catalogoOpen;

  const loadVehiculo = useCallback(async () => {
    if (!isEdit || !patenteDecoded) return;
    try {
      setLoading(true);
      const veh = await vehiculoService.getVehiculo(patenteDecoded);
      setFormData({
        patente: veh.patente || patenteDecoded,
        nroCliente: veh.nroCliente || 0,
        nombreCliente: veh.nombreCliente || '',
        descripcion: veh.descripcion || '',
        kilometrajeUltimoService: veh.kilometrajeUltimoService ?? null,
        fechaUltimoService: toDateInput(veh.fechaUltimoService),
        fechaProximoService: toDateInput(veh.fechaProximoService),
        anio: veh.anio ?? null,
        nroSerieMotor: veh.nroSerieMotor || '',
        nroSerieChasis: veh.nroSerieChasis || '',
        codigoTecnico: veh.codigoTecnico || '',
        descripcionTecnico: veh.descripcionTecnico || '',
        codigoMarca: veh.codigoMarca || '',
        descripcionMarca: veh.descripcionMarca || '',
        codigoLinea: veh.codigoLinea || '',
        descripcionLinea: veh.descripcionLinea || '',
        codigoModelo: veh.codigoModelo || '',
        descripcionModelo: veh.descripcionModelo || '',
        codigoCombustible: veh.codigoCombustible || '',
        descripcionCombustible: veh.descripcionCombustible || '',
      });
    } catch {
      enqueueSnackbar('Error al cargar la unidad', { variant: 'error' });
      navigate('/servicio/vehiculos');
    } finally {
      setLoading(false);
    }
  }, [isEdit, patenteDecoded, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) loadVehiculo();
  }, [isEdit, loadVehiculo]);

  const handleCancel = useCallback(() => {
    if (saving) return;
    navigate('/servicio/vehiculos');
  }, [navigate, saving]);

  const buildPayload = (): CreateVehiculoDto => ({
    patente: formData.patente.trim().slice(0, MAX_UNIDAD_CODIGO),
    nroCliente: formData.nroCliente,
    descripcion: formData.descripcion.trim() || undefined,
    kilometrajeUltimoService: formData.kilometrajeUltimoService || null,
    fechaUltimoService: formData.fechaUltimoService || null,
    fechaProximoService: formData.fechaProximoService || null,
    anio: formData.anio ?? null,
    nroSerieMotor: formData.nroSerieMotor?.trim() || '',
    nroSerieChasis: formData.nroSerieChasis?.trim() || '',
    codigoTecnico: formData.codigoTecnico?.trim() || '',
    codigoMarca: formData.codigoMarca?.trim() || '',
    codigoLinea: formData.codigoLinea?.trim() || '',
    codigoModelo: formData.codigoModelo?.trim() || '',
    codigoCombustible: formData.codigoCombustible?.trim() || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    try {
      setSaving(true);
      if (!formData.patente?.trim()) {
        enqueueSnackbar('El identificador es requerido', { variant: 'warning' });
        return;
      }
      if (!formData.nroCliente) {
        enqueueSnackbar('El cliente es requerido', { variant: 'warning' });
        return;
      }

      const payload = buildPayload();
      if (isEdit) {
        const { patente: _omitPatente, ...updateDto } = payload;
        await vehiculoService.updateVehiculo(patenteDecoded, updateDto);
      } else {
        await vehiculoService.createVehiculo(payload);
      }
      enqueueSnackbar('Unidad guardada exitosamente', { variant: 'success' });
      navigate('/servicio/vehiculos');
    } catch (error: unknown) {
      console.error('Error al guardar unidad:', error);
      const err = error as {
        response?: { data?: { message?: string; errors?: { general?: string[] } } };
      };
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.general?.[0] ||
        'Error al guardar la unidad';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (dialogsOpen) return;
      if (e.key === 'F2') {
        e.preventDefault();
        if (!saving) formRef.current?.requestSubmit();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleCancel, saving, dialogsOpen]);

  const displayCatalogo = (codigo?: string, desc?: string) => {
    const c = (codigo || '').trim();
    const d = (desc || '').trim();
    if (c && d) return `${c} - ${d}`;
    return c || d || '';
  };

  const loadTecnicos = useCallback(async (searchTerm: string): Promise<CatalogoItemDto[]> => {
    const data = await tecnicoService.getTecnicos({
      searchTerm: searchTerm || undefined,
      pageNumber: 1,
      pageSize: 50,
    });
    return data.items.map((t) => ({
      codigo: t.codigoTecnico,
      descripcion: t.descripcion,
    }));
  }, []);

  const handleCatalogoSelect = (item: CatalogoItemDto) => {
    switch (catalogoOpen) {
      case 'tecnico':
        setFormData((prev) => ({
          ...prev,
          codigoTecnico: item.codigo,
          descripcionTecnico: item.descripcion,
        }));
        break;
      case 'marca':
        setFormData((prev) => ({
          ...prev,
          codigoMarca: item.codigo,
          descripcionMarca: item.descripcion,
          codigoLinea: '',
          descripcionLinea: '',
          codigoModelo: '',
          descripcionModelo: '',
        }));
        break;
      case 'linea':
        setFormData((prev) => ({
          ...prev,
          codigoLinea: item.codigo,
          descripcionLinea: item.descripcion,
          codigoModelo: '',
          descripcionModelo: '',
        }));
        break;
      case 'modelo':
        setFormData((prev) => ({
          ...prev,
          codigoModelo: item.codigo,
          descripcionModelo: item.descripcion,
        }));
        break;
      case 'combustible':
        setFormData((prev) => ({
          ...prev,
          codigoCombustible: item.codigo,
          descripcionCombustible: item.descripcion,
        }));
        break;
      default:
        break;
    }
  };

  const openLinea = () => {
    if (!formData.codigoMarca?.trim()) {
      enqueueSnackbar('Seleccione primero una marca', { variant: 'warning' });
      return;
    }
    setCatalogoOpen('linea');
  };

  const openModelo = () => {
    if (!formData.codigoMarca?.trim() || !formData.codigoLinea?.trim()) {
      enqueueSnackbar('Seleccione primero marca y línea', { variant: 'warning' });
      return;
    }
    setCatalogoOpen('modelo');
  };

  const marcaCodigo = formData.codigoMarca?.trim() || '';
  const lineaCodigo = formData.codigoLinea?.trim() || '';

  const crudKind: CatalogoCrudKind | null =
    catalogoOpen === 'marca' ||
    catalogoOpen === 'linea' ||
    catalogoOpen === 'modelo' ||
    catalogoOpen === 'combustible'
      ? catalogoOpen
      : null;

  const crudConfig = useMemo(() => {
    if (!crudKind) return null;
    switch (crudKind) {
      case 'marca':
        return {
          title: 'Marcas',
          codigoLabel: 'Código de Marca',
          nombreLabel: 'Nombre de Marca',
          list: (term: string) => vehiculoService.getMarcas(term || undefined),
          create: (codigo: string, descripcion: string) =>
            vehiculoService.createMarca({ codigo, descripcion }),
          update: (codigo: string, descripcion: string) =>
            vehiculoService.updateMarca(codigo, { descripcion }),
          remove: (codigo: string) => vehiculoService.deleteMarca(codigo),
        };
      case 'linea':
        return {
          title: 'Líneas',
          codigoLabel: 'Código de Línea',
          nombreLabel: 'Nombre de Línea',
          list: (term: string) => vehiculoService.getLineas(marcaCodigo, term || undefined),
          create: (codigo: string, descripcion: string) =>
            vehiculoService.createLinea({ codigo, descripcion, codigoMarca: marcaCodigo }),
          update: (codigo: string, descripcion: string) =>
            vehiculoService.updateLinea(codigo, marcaCodigo, { descripcion }),
          remove: (codigo: string) => vehiculoService.deleteLinea(codigo, marcaCodigo),
        };
      case 'modelo':
        return {
          title: 'Modelos',
          codigoLabel: 'Código de Modelo',
          nombreLabel: 'Nombre de Modelo',
          list: (term: string) =>
            vehiculoService.getModelos(marcaCodigo, lineaCodigo, term || undefined),
          create: (codigo: string, descripcion: string) =>
            vehiculoService.createModelo({
              codigo,
              descripcion,
              codigoMarca: marcaCodigo,
              codigoLinea: lineaCodigo,
            }),
          update: (codigo: string, descripcion: string) =>
            vehiculoService.updateModelo(codigo, marcaCodigo, lineaCodigo, { descripcion }),
          remove: (codigo: string) =>
            vehiculoService.deleteModelo(codigo, marcaCodigo, lineaCodigo),
        };
      case 'combustible':
        return {
          title: 'Combustibles',
          codigoLabel: 'Código de Combustible',
          nombreLabel: 'Nombre',
          list: (term: string) => vehiculoService.getCombustibles(term || undefined),
          create: (codigo: string, descripcion: string) =>
            vehiculoService.createCombustible({ codigo, descripcion }),
          update: (codigo: string, descripcion: string) =>
            vehiculoService.updateCombustible(codigo, { descripcion }),
          remove: (codigo: string) => vehiculoService.deleteCombustible(codigo),
        };
      default:
        return null;
    }
  }, [crudKind, marcaCodigo, lineaCodigo]);

  const lookupField = (
    label: string,
    value: string,
    onSearch: () => void,
  ) => (
    <TextField
      label={label}
      value={value}
      fullWidth
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={onSearch} edge="end" color="primary" aria-label={`Buscar ${label}`}>
              <SearchIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    />
  );

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ textTransform: 'none' }}>
              Unidades
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isEdit ? 'Editar Unidad' : 'Nueva Unidad'}
            </Typography>
          </Stack>

          <form ref={formRef} onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {lookupField(
                  'Cliente',
                  formData.nroCliente
                    ? `${formData.nroCliente}${formData.nombreCliente ? ` - ${formData.nombreCliente}` : ''}`
                    : '',
                  () => setOpenClienteSearch(true),
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Identificador"
                  value={formData.patente}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      patente: e.target.value.slice(0, MAX_UNIDAD_CODIGO),
                    }))
                  }
                  onBlur={() =>
                    setFormData((prev) => ({
                      ...prev,
                      patente: prev.patente.trim().slice(0, MAX_UNIDAD_CODIGO),
                    }))
                  }
                  fullWidth
                  required
                  disabled={isEdit}
                  autoFocus={!isEdit}
                  inputProps={{ maxLength: MAX_UNIDAD_CODIGO }}
                  helperText={`Hasta ${MAX_UNIDAD_CODIGO} caracteres`}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descripción"
                  value={formData.descripcion}
                  onChange={(e) => setFormData((prev) => ({ ...prev, descripcion: e.target.value }))}
                  fullWidth
                  autoFocus={isEdit}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Fecha Último Service"
                  type="date"
                  value={formData.fechaUltimoService || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, fechaUltimoService: e.target.value }))
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Fecha Próximo Service"
                  type="date"
                  value={formData.fechaProximoService || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, fechaProximoService: e.target.value }))
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Accordion
              defaultExpanded={false}
              disableGutters
              sx={{
                mt: 3,
                border: '1px solid #e0e0e0',
                borderRadius: '8px !important',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: '#f8f9fa',
                  minHeight: 56,
                  '& .MuiAccordionSummary-content': { my: 1.5 },
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Características Automotor
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2, pb: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                      <TextField
                        label="Kilometraje Último Service"
                        value={
                          formData.kilometrajeUltimoService == null
                            ? ''
                            : String(formData.kilometrajeUltimoService)
                        }
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 7);
                          setFormData((prev) => ({
                            ...prev,
                            kilometrajeUltimoService: digits === '' ? null : Number(digits),
                          }));
                        }}
                        fullWidth
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 7 }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Año"
                        value={formData.anio ?? ''}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setFormData((prev) => ({
                            ...prev,
                            anio: digits === '' ? null : Number(digits),
                          }));
                        }}
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          maxLength: 4,
                        }}
                        fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Nro Serie Motor"
                        value={formData.nroSerieMotor || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, nroSerieMotor: e.target.value }))
                        }
                        fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Nro Serie Chasis"
                        value={formData.nroSerieChasis || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, nroSerieChasis: e.target.value }))
                        }
                        fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      {lookupField(
                        'Técnico Habitual',
                        displayCatalogo(formData.codigoTecnico, formData.descripcionTecnico),
                        () => setCatalogoOpen('tecnico'),
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                      {lookupField(
                        'Marca',
                        displayCatalogo(formData.codigoMarca, formData.descripcionMarca),
                        () => setCatalogoOpen('marca'),
                      )}
                      {lookupField(
                        'Línea',
                        displayCatalogo(formData.codigoLinea, formData.descripcionLinea),
                        openLinea,
                      )}
                      {lookupField(
                        'Modelo',
                        displayCatalogo(formData.codigoModelo, formData.descripcionModelo),
                        openModelo,
                      )}
                      {lookupField(
                        'Combustible',
                        displayCatalogo(formData.codigoCombustible, formData.descripcionCombustible),
                        () => setCatalogoOpen('combustible'),
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'F2-Aceptar'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                ESC-Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ClienteSearch
        open={openClienteSearch}
        onClose={() => setOpenClienteSearch(false)}
        onSelect={(cliente) => {
          setFormData((prev) => ({
            ...prev,
            nroCliente: cliente.nroCliente,
            nombreCliente: cliente.razonSocial || cliente.nombre || '',
          }));
          setOpenClienteSearch(false);
        }}
      />

      <CatalogoSearchDialog
        open={catalogoOpen === 'tecnico'}
        onClose={() => setCatalogoOpen(null)}
        onSelect={handleCatalogoSelect}
        title="Buscar Técnico"
        loadItems={loadTecnicos}
        searchPlaceholder="Buscar técnico..."
      />

      {crudConfig && (
        <CatalogoCrudDialog
          open={!!crudKind}
          onClose={() => setCatalogoOpen(null)}
          onSelect={handleCatalogoSelect}
          title={crudConfig.title}
          codigoLabel={crudConfig.codigoLabel}
          nombreLabel={crudConfig.nombreLabel}
          list={crudConfig.list}
          create={crudConfig.create}
          update={crudConfig.update}
          remove={crudConfig.remove}
        />
      )}
    </Box>
  );
};

export default VehiculoForm;
